import { useTheme } from "@/src/styles/theme-provider";
import { Calendar, Edit2, Gift, Star1 } from "iconsax-react-nativejs";
import { useTranslation } from "react-i18next";
import { Pressable, Text, View } from "react-native";
import ScButton from "../../ui-kit/button";

type ActiveSeasonCardProps = Readonly<{
	season: {
		ItemId: string;
		name: string;
		startDate?: string;
		endDate?: string;
		maxGiveAmt?: number;
		minGiveAmt?: number;
		seasonAllowance?: number;
		status?: string;
	};
	onEndSeason: (seasonId: string) => Promise<void>;
	isEnding?: boolean;
	/** When provided, the card becomes pressable and opens the edit view. */
	onEdit?: () => void;
}>;

const StatCell = ({ icon, label, value, accent = false }: { icon: React.ReactNode; label: string; value: string; accent?: boolean }) => (
	<View className="flex-1 flex-col gap-1 px-4 py-4 md:px-7 md:py-6 border-r border-ash-3 last:border-r-0">
		<View className="flex-row items-center gap-1">
			{icon}
			<Text className="text-xs text-ash-2">{label}</Text>
		</View>
		<Text className={`text-lg md:text-xl font-medium ${accent ? "text-jade" : "text-onyx"}`}>{value}</Text>
	</View>
);

const ActiveSeasonCard = ({ season, onEndSeason, isEnding = false, onEdit }: ActiveSeasonCardProps) => {
	const { t } = useTranslation();
	const { themeVars } = useTheme();
	const ashColor = themeVars["--color-ash-2"] as string;

	return (
		<Pressable
			onPress={onEdit}
			disabled={!onEdit}
			className="bg-chalk rounded-xl md:rounded-2xl md:border md:border-ash-3 overflow-hidden w-full"
		>
			{/* Header */}
			<View className="px-5 pt-5 pb-4 md:px-7 md:pt-6 md:pb-5 gap-2">
				<View className="flex-row items-center justify-between gap-2">
					<View className="flex-row items-center gap-2 self-start px-3 py-1 rounded-full border border-jade">
						<View className="w-1.5 h-1.5 rounded-full bg-jade" />
						<Text className="text-xs text-jade font-medium">{t("LABEL_ACTIVE")}</Text>
					</View>
					{onEdit && (
						<View className="flex-row items-center gap-1">
							<Edit2 size={15} color={themeVars["--color-primary-default"] as string} />
							<Text className="text-xs text-primary font-medium">{t("BTN_EDIT")}</Text>
						</View>
					)}
				</View>
				<Text className="text-xl md:text-2xl font-medium text-onyx">{season.name}</Text>
				<Text className="text-sm text-ash-2">{t("LABEL_ACTIVE_SEASON_IN_PROGRESS")}</Text>
			</View>

			<View className="h-px bg-ash-3" />

			{/* Stats row */}
			<View className="flex-row">
				<StatCell
					icon={<Star1 size={14} color={ashColor} />}
					label={t("LABEL_TRANSACTION_STAR_RANGE")}
					value={`${season.minGiveAmt ?? 0} – ${season.maxGiveAmt ?? 0}`}
				/>
				<StatCell
					icon={<Gift size={14} color={ashColor} />}
					label={t("LABEL_ALLOWANCE")}
					value={`${season.seasonAllowance ?? 0} ${t("LABEL_STARS_UNIT")}`}
					accent
				/>
			</View>

			<View className="h-px bg-ash-3" />

			{/* Footer */}
			<View className="flex-row flex-wrap items-center justify-between gap-3 px-5 py-4 md:px-7 md:py-5">
				<View className="flex-row items-center gap-1.5">
					<Calendar size={15} color={ashColor} />
					<Text className="text-sm text-ash-2">
						{t("LABEL_SEASON_STARTED")}{" "}
						<Text className="text-onyx font-medium">
							{season.startDate ? new Date(season.startDate).toLocaleDateString() : "—"}
						</Text>
					</Text>
				</View>
				<ScButton
					text="BTN_END_SEASON"
					onPress={() => onEndSeason(season.ItemId)}
					loading={isEnding}
					mode="outlined"
					className="border-crimson"
					textClassName="text-crimson-1"
				/>
			</View>
		</Pressable>
	);
};

export default ActiveSeasonCard;
